/**
 * useAccounts Hook
 * Manages accounts (customer organizations)
 */

import { useState, useEffect, useCallback } from 'react'
import { useApp } from '../context'

export function useAccounts() {
  const { db } = useApp()
  const [accounts, setAccounts] = useState([])
  const [currentAccount, setCurrentAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load all accounts
  const loadAccounts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await db.getAccounts()
      setAccounts(list)
      // Set current account to default or first one if not set
      if (!currentAccount && list.length > 0) {
        const defaultAccount = list.find(a => a.is_default) || list[0]
        setCurrentAccount(defaultAccount)
      }
    } catch (err) {
      setError(err.message)
      console.error('Failed to load accounts:', err)
    } finally {
      setLoading(false)
    }
  }, [db, currentAccount])

  // Load accounts on mount
  useEffect(() => {
    loadAccounts()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Get a single account by ID
  const getAccount = useCallback(async (id) => {
    try {
      return await db.getAccount(id)
    } catch (err) {
      setError(err.message)
      return null
    }
  }, [db])

  // Create a new account
  const createAccount = useCallback(async (data) => {
    setError(null)
    try {
      const account = await db.createAccount(data)
      setAccounts(prev => [...prev, account])
      return account
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db])

  // Update an account
  const updateAccount = useCallback(async (id, data) => {
    setError(null)
    try {
      const account = await db.updateAccount(id, data)
      setAccounts(prev => prev.map(a => a.id === id ? account : a))
      if (currentAccount?.id === id) {
        setCurrentAccount(account)
      }
      return account
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db, currentAccount])

  // Delete an account
  const deleteAccount = useCallback(async (id) => {
    setError(null)
    try {
      await db.deleteAccount(id)
      setAccounts(prev => prev.filter(a => a.id !== id))
      if (currentAccount?.id === id) {
        setCurrentAccount(accounts.find(a => a.id !== id) || null)
      }
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db, currentAccount, accounts])

  // Select an account as current
  const selectAccount = useCallback((account) => {
    setCurrentAccount(account)
  }, [])

  return {
    // State
    accounts,
    currentAccount,
    loading,
    error,

    // Actions
    loadAccounts,
    getAccount,
    createAccount,
    updateAccount,
    deleteAccount,
    selectAccount,
  }
}

export default useAccounts
